import React from 'react';
import { Icon, InfoMessage } from '@fasstech/middle-design-system';
import { find, propEq } from 'ramda';
import classNames from 'classnames';
import { isNotNil, mapIndexed } from '@@serverHelpers/index.mjs';
import { roundPrice as round } from '@@views/_common/server/helpers/groupHealthPricing/roundPrice.mjs';

const Budget = ({
  getMiddleText,
  displayedPricings,
  selectedTariffStructure,
  isSelected,
  form
}) => {
  const beneficiariesEnrollment = form.watch('beneficiariesEnrollment');
  const employerPercentShare = form.watch('employerPercentShare');

  return (
    <div
      className={'grid border rounded-xl shadow-lg mt-4 w-full'}
      style={{
        gridTemplateColumns: `300px repeat(${displayedPricings.length}, minmax(150px, 1fr))`
      }}>
      {/* Titre général */}
      <div className={'col-span-full flex flex-col gap-4 p-4 sticky top-0 z-20 bg-white'}>
        <span className={'font-bold'}>
          {getMiddleText('NIVEAUXGARANTIESETTARIFS_BUDGET_TOTAL_ESTIMATIF_LABEL')}
        </span>
        <InfoMessage iconName={'lightbulb'} className={'w-fit'}>
          {getMiddleText('NIVEAUXGARANTIESETTARIFS_BUDGET_TOTAL_MESSAGE_INFO')}
        </InfoMessage>
      </div>

      {/* Ligne 1 : total estimatif */}
      <div className={'p-4 font-bold bg-white sticky left-0 z-10 flex items-center'}>
        {getMiddleText('NIVEAUXGARANTIESETTARIFS_BUDGET_TOTAL_LABEL')}
      </div>
      {mapIndexed(({ pricingLabel, totalPrices }, index) => {
        const isLast = index === displayedPricings.length - 1;
        const priceEntry =
          find(propEq('tariffStructureCode', selectedTariffStructure))(totalPrices || []) || {};
        const amount = beneficiariesEnrollment ? priceEntry.withBenef : priceEntry.withoutBenef;

        return (
          <div
            key={`total-${pricingLabel}`}
            className={classNames('flex flex-col mx-1 items-center justify-center bg-white p-4', {
              'border-r border-l': !isLast,
              'border-l': isLast,
              'border-primary': isSelected(pricingLabel)
            })}>
            {isNotNil(amount) ? (
              <div className={'border rounded-xl p-2 mt-4 w-fit text-center'}>
                <div className="text-lg">
                  <span className={'font-semibold'}>{round(amount)} € </span>/ mois
                </div>
                <div className={'text-sm'}>{round(amount * 12)} € / an</div>
              </div>
            ) : (
              <div>-</div>
            )}
          </div>
        );
      })(displayedPricings)}

      {/* Ligne 2 : part employeur */}
      <div className={'p-4 font-bold bg-white sticky left-0 z-10 flex flex-col justify-center'}>
        <span
          dangerouslySetInnerHTML={{
            __html: getMiddleText('NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_BLOC_TOTAL_LABEL', {
              employerPercentShare: `${employerPercentShare}%`
            })
          }}
        />
        {beneficiariesEnrollment && (
          <div className={'flex gap-2 items-start mt-2 text-sm'}>
            <Icon name={'indicator_valid'} wrapperClassName={'mt-1'} />
            <div>{getMiddleText('NIVEAUXGARANTIESETTARIFS_PART_EMPLOYEUR_AYANT_DROIT_LABEL')}</div>
          </div>
        )}
      </div>
      {mapIndexed(({ pricingLabel, totalPrices }, index) => {
        const isLast = index === displayedPricings.length - 1;
        const priceEntry =
          find(propEq('tariffStructureCode', selectedTariffStructure))(totalPrices || []) || {};
        const amount = beneficiariesEnrollment ? priceEntry.withBenef : priceEntry.withoutBenef;
        const employerAmount =
          isNotNil(amount) && isNotNil(employerPercentShare)
            ? round(amount * (employerPercentShare / 100))
            : null;

        return (
          <div
            key={`employer-${pricingLabel}`}
            className={classNames('flex flex-col mx-1 items-center justify-center bg-white p-4', {
              'border-r border-l': !isLast,
              'border-l': isLast,
              'border-primary': isSelected(pricingLabel)
            })}>
            {isNotNil(employerAmount) ? (
              <div className={'border rounded-xl p-2 mt-4 w-fit text-center'}>
                <div className={'text-lg'}>
                  <span className={'font-semibold'}>{employerAmount} € </span>/ mois
                </div>
                <div className={'text-sm'}>{round(employerAmount * 12)} € / an</div>
              </div>
            ) : (
              <div>-</div>
            )}
          </div>
        );
      })(displayedPricings)}
    </div>
  );
};

export default Budget;
